/**
 * ImageSearchPane — the image-search surface (a full-pane "Images" view).
 *
 * Browse the bound library's figures as a thumbnail grid, filter by source file
 * and caption keyword, click a thumbnail to enlarge it (lightbox), and insert a
 * figure into the Word document at the cursor. Stateless — images are raw
 * library assets; nothing is persisted here.
 *
 * Thumbnails/insert bytes come from the server's cached /api/find-image proxy
 * via imageSearchApi (fetch-with-Bearer → object URL / data-URI), because an
 * <img> tag can't carry the auth header. Insert itself is shell-owned (Office.js
 * + a writer-facing note), so the pane calls back via ``onInsert(title)``.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useState } from "react";
import { type ImageItem, fetchImageObjectUrl, listImages } from "../../api/imageSearchApi";
import { fonts, inkColors, surfaceColors } from "../tokens";

export interface ImageSearchPaneProps {
  libraryId: string | null;
  libraryName?: string | null;
  /** Insert or replace the figure with this caption in the doc (shell-owned Office.js).
   *  When replaceMode is true, the Insert button will replace an existing image instead
   *  of inserting a new one. */
  onInsert: (title: string, replaceMode?: boolean) => void;
  /** When true, the pane is in "replace image" mode. Default false. */
  replaceMode?: boolean;
}

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", minHeight: 0, flexGrow: 1, background: surfaceColors.paper },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  title: { fontFamily: fonts.display, fontSize: "12.5px", fontWeight: 700, color: inkColors.ink },
  chip: {
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    fontWeight: 600,
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "10px",
    padding: "1px 8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "180px",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  search: {
    flexGrow: 1,
    minWidth: "120px",
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink,
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    padding: "6px 9px",
  },
  select: {
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: inkColors.ink2,
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    padding: "6px 8px",
    maxWidth: "190px",
  },
  scroll: { flexGrow: 1, minHeight: 0, overflowY: "auto" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: "10px",
    padding: "10px 12px",
  },
  card: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    background: surfaceColors.paper,
  },
  thumbButton: {
    display: "block",
    width: "100%",
    height: "96px",
    padding: 0,
    margin: 0,
    border: "none",
    borderBottom: `1px solid ${surfaceColors.line}`,
    background: surfaceColors.paperDim,
    cursor: "zoom-in",
    overflow: "hidden",
  },
  thumb: { maxWidth: "100%", maxHeight: "96px", objectFit: "contain" },
  thumbPlaceholder: { fontFamily: fonts.mono, fontSize: "9.5px", color: inkColors.ink4 },
  meta: { padding: "6px 8px", display: "flex", flexDirection: "column", gap: "3px" },
  caption: {
    fontFamily: fonts.body,
    fontSize: "11.5px",
    fontWeight: 600,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sub: { fontFamily: fonts.mono, fontSize: "9px", color: inkColors.ink4 },
  insertButton: {
    margin: "4px 8px 8px",
    fontFamily: fonts.mono,
    fontSize: "9px",
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    borderRadius: "5px",
    padding: "5px 8px",
    cursor: "pointer",
    border: `1px solid ${surfaceColors.line}`,
    background: surfaceColors.paperDim,
    color: inkColors.ink2,
    ":hover": { color: inkColors.ink },
  },
  status: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink4, padding: "14px 12px" },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: "rgba(0, 0, 0, 0.82)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "16px",
    border: "none",
    width: "100%",
    height: "100%",
    cursor: "zoom-out",
  },
  lightboxInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    maxWidth: "100%",
    cursor: "default",
  },
  lightboxImg: { maxWidth: "100%", maxHeight: "68vh", objectFit: "contain", borderRadius: "4px", background: "#fff" },
  lightboxCaption: { fontFamily: fonts.body, fontSize: "12px", color: "#fff", textAlign: "center", maxWidth: "92%" },
  lightboxBar: { display: "flex", gap: "8px" },
  lightboxBtn: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    borderRadius: "5px",
    padding: "7px 13px",
    cursor: "pointer",
    border: "1px solid rgba(255, 255, 255, 0.45)",
    background: "rgba(255, 255, 255, 0.12)",
    color: "#fff",
    ":hover": { background: "rgba(255, 255, 255, 0.24)" },
  },
});

/** Fetch one image's bytes through the cached proxy; revoke the object URL on unmount/change. */
function useImageObjectUrl(libraryId: string | null, title: string): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!libraryId) {
      setSrc(null);
      return;
    }
    let url: string | null = null;
    let cancelled = false;
    fetchImageObjectUrl(libraryId, title)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setSrc(u);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [libraryId, title]);
  return src;
}

function subline(image: ImageItem): string {
  return [image.file_name].filter(Boolean).join(" · ");
}

const ImageCard: React.FC<{
  libraryId: string;
  image: ImageItem;
  onInsert: (title: string, replaceMode?: boolean) => void;
  onZoom: (image: ImageItem) => void;
  replaceMode?: boolean;
}> = ({ libraryId, image, onInsert, onZoom, replaceMode }) => {
  const styles = useStyles();
  const src = useImageObjectUrl(libraryId, image.title);
  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.thumbButton}
        onClick={() => onZoom(image)}
        aria-label={`Enlarge ${image.title || "figure"}`}
        title="Click to enlarge"
      >
        {src ? (
          <img className={styles.thumb} src={src} alt={image.title || "figure"} />
        ) : (
          <span className={styles.thumbPlaceholder}>…</span>
        )}
      </button>
      <div className={styles.meta}>
        <span className={styles.caption} title={image.title}>
          {image.title || "(untitled)"}
        </span>
        <span className={styles.sub} title={subline(image)}>
          {subline(image)}
        </span>
      </div>
      <button type="button" className={styles.insertButton} onClick={() => onInsert(image.title, replaceMode)}>
        {replaceMode ? "replace" : "insert"}
      </button>
    </div>
  );
};

const ImageLightbox: React.FC<{
  libraryId: string;
  image: ImageItem;
  onInsert: (title: string, replaceMode?: boolean) => void;
  onClose: () => void;
  replaceMode?: boolean;
}> = ({ libraryId, image, onInsert, onClose, replaceMode }) => {
  const styles = useStyles();
  const src = useImageObjectUrl(libraryId, image.title);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <button type="button" className={styles.overlay} onClick={onClose} aria-label="Close enlarged figure">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only; keyboard close is the backdrop button + Escape */}
      <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
        {src ? (
          <img className={styles.lightboxImg} src={src} alt={image.title || "figure"} />
        ) : (
          <span className={styles.lightboxCaption}>Loading…</span>
        )}
        <span className={styles.lightboxCaption}>{[image.title, subline(image)].filter(Boolean).join(" — ")}</span>
        <div className={styles.lightboxBar}>
          <button
            type="button"
            className={styles.lightboxBtn}
            onClick={() => {
              onInsert(image.title, replaceMode);
              onClose();
            }}
          >
            {replaceMode ? "replace" : "insert"}
          </button>
          <button type="button" className={styles.lightboxBtn} onClick={onClose}>
            close
          </button>
        </div>
      </div>
    </button>
  );
};

export const ImageSearchPane: React.FC<ImageSearchPaneProps> = ({ libraryId, libraryName, onInsert, replaceMode }) => {
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const [fileId, setFileId] = useState("");
  const [images, setImages] = useState<ImageItem[]>([]);
  const [files, setFiles] = useState<{ file_id: string; file_name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState<ImageItem | null>(null);

  // Fetch on library / file / (debounced) query change. The file dropdown is
  // refreshed only on an unfiltered fetch so a narrow query can't empty it.
  useEffect(() => {
    if (!libraryId) {
      setImages([]);
      setFiles([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(() => {
      listImages(libraryId, { fileId: fileId || null, q: query || null })
        .then((res) => {
          if (cancelled) return;
          setImages(res.images);
          if (!query) setFiles(res.files);
          setError(null);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setImages([]);
          setError(e instanceof Error ? e.message : "request failed");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [libraryId, fileId, query]);

  let bodyContent: React.ReactNode;
  if (!libraryId) {
    bodyContent = <div className={styles.status}>Select a library first — figures are listed per library.</div>;
  } else if (error) {
    bodyContent = <div className={styles.status}>Couldn't load images — {error}</div>;
  } else if (loading && images.length === 0) {
    bodyContent = <div className={styles.status}>Loading figures…</div>;
  } else if (images.length === 0) {
    bodyContent = <div className={styles.status}>No figures found. Try a different keyword or file.</div>;
  } else {
    bodyContent = (
      <div className={styles.grid}>
        {images.map((img) => (
          <ImageCard
            key={`${img.file_id}:${img.title}`}
            libraryId={libraryId}
            image={img}
            onInsert={onInsert}
            onZoom={setZoomed}
            replaceMode={replaceMode}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>Image search</span>
        {libraryId && (
          <span className={styles.chip} title={`Library: ${libraryName ?? libraryId}`}>
            {libraryName ?? libraryId}
          </span>
        )}
      </div>
      {libraryId && (
        <div className={styles.controls}>
          <input
            className={mergeClasses(styles.search)}
            type="search"
            placeholder="Search figures by caption…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            className={styles.select}
            value={fileId}
            onChange={(e) => setFileId(e.target.value)}
            aria-label="Filter by source file"
          >
            <option value="">All files</option>
            {files.map((f) => (
              <option key={f.file_id} value={f.file_id}>
                {f.file_name || f.file_id}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className={styles.scroll}>{bodyContent}</div>
      {zoomed && libraryId && (
        <ImageLightbox libraryId={libraryId} image={zoomed} onInsert={onInsert} onClose={() => setZoomed(null)} replaceMode={replaceMode} />
      )}
    </div>
  );
};

export default ImageSearchPane;
